import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusAccessREquest } from "@/enum/status";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { Table } from "@tanstack/react-table";
import { debounce } from "lodash";
import { X } from "lucide-react";
import React, { useCallback } from "react";
import { DateRange } from "react-day-picker";
import { useAccessRequestFilterActions } from "../ducks/filter/hooks/actions";
import { ACCESS_REQUEST_STATUS_BADGE } from "@/constants/messages";
import { DataTableViewOptions } from "./data-table-view-options";
import { DateRangePicker, DateRangePickerRef } from "./date-range-picker";
import { applyCpfMaskIfNeeded } from "@/utils/cpf-mask";
import { SelectInput } from "components/Select";
import { getSelectClasses } from "@/utils/filter-styles";
import { formatOptionLabel } from "@/utils/format-option-label";
import { MultiValueTag } from "@/components/ui/MultiValueTag";
import { formatFilterValue } from "@/utils/formatters";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

const statusOptions = Object.values(StatusAccessREquest).map((value) => ({
  value,
  label: ACCESS_REQUEST_STATUS_BADGE[value].label,
  variant: ACCESS_REQUEST_STATUS_BADGE[value].variant,
}));

export function DataTableToolbar<TData>({
  table,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const { updateFilter } = useAccessRequestFilterActions();

  const [searchValue, setSearchValue] = React.useState("");
  const [storeValue, setStoreValue] = React.useState<any[]>([]);
  const [statusValue, setStatusValue] = React.useState("all");
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(
    undefined,
  );

  const debouncedUpdateFilter = useCallback(
    debounce((value: string) => {
      updateFilter({ key: "search", value });
      updateFilter({ key: "meta", value: { page: 1 } });
    }, 500),
    [updateFilter],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = applyCpfMaskIfNeeded(event.target.value);
    setSearchValue(value);
    debouncedUpdateFilter(value);
  };

  const handleStoreChange = (options: any) => {
    const selectedOptions = Array.isArray(options)
      ? options
      : options
        ? [options]
        : [];
    setStoreValue(selectedOptions);
    const values = selectedOptions.map((opt: any) => opt.value);
    updateFilter({ key: "store_code", value: formatFilterValue(values) || "" });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleStatusChange = (value: string) => {
    setStatusValue(value);
    const statusCode = value === "all" ? "" : value;
    updateFilter({ key: "status", value: statusCode });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleDateRangeChange = useCallback(
    (startDate: string | undefined, endDate: string | undefined) => {
      setDateRange({
        from: startDate ? new Date(startDate) : undefined,
        to: endDate ? new Date(endDate) : undefined,
      });
      updateFilter({ key: "from_date", value: startDate || "" });
      updateFilter({ key: "to_date", value: endDate || "" });
      updateFilter({ key: "meta", value: { page: 1 } });
    },
    [updateFilter],
  );

  const dateRangePickerRef = React.useRef<DateRangePickerRef>(null);

  const clearAllFilters = () => {
    setSearchValue("");
    setStoreValue([]);
    setStatusValue("all");
    setDateRange(undefined);
    updateFilter({ key: "search", value: "" });
    updateFilter({ key: "store_code", value: "" });
    updateFilter({ key: "status", value: "" });
    updateFilter({ key: "from_date", value: "" });
    updateFilter({ key: "to_date", value: "" });
    updateFilter({ key: "orderBy", value: {} });
    updateFilter({ key: "meta", value: { page: 1 } });
    dateRangePickerRef.current?.clear();
    table.resetColumnFilters();
  };

  const { data: storeOptions = [] } = useGetStoresOptionsQuery();

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between w-full">
      <div className="flex flex-1 flex-wrap items-end gap-2">
        <div className="relative">
          <Input
            value={searchValue}
            onChange={handleInputChange}
            className="h-8 w-[200px] pt-2"
            placeholder=" "
          />
          <label className="absolute left-2 -top-2 bg-white px-1 text-xs font-medium text-gray-700">
            Buscar por Nome ou CPF
          </label>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="store_code"
            className="text-sm font-medium text-gray-700"
          >
            Loja
          </label>
          <SelectInput
            options={storeOptions || []}
            placeholder="Selecionar loja"
            value={storeValue}
            onChange={handleStoreChange}
            className={`${getSelectClasses(storeValue.length > 0)} h-8 min-w-[180px]`}
            classNamePrefix="an-simple-select"
            isMulti
            isClearable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ MultiValue: MultiValueTag }}
            formatOptionLabel={formatOptionLabel}
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="status" className="text-sm font-medium text-gray-700">
            Status
          </label>
          <Select value={statusValue} onValueChange={handleStatusChange}>
            <SelectTrigger id="status" className="h-8 w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {statusOptions.map((statusOption) => (
                <SelectItem key={statusOption.value} value={statusOption.value}>
                  <div className="flex items-center">
                    <Badge
                      className="mr-2 h-2 w-2 rounded-full p-0"
                      variant={statusOption.variant as any}
                    />
                    {statusOption.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="date_range"
            className="text-sm font-medium text-gray-700"
          >
            Data
          </label>
          <DateRangePicker
            ref={dateRangePickerRef}
            onDateChange={handleDateRangeChange}
            initialDateRange={dateRange}
          />
        </div>

        {isFiltered && (
          <Button
            variant="ghost_disabled"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2"
          >
            Resetar
            <X className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={clearAllFilters}
          className="h-8 px-2 text-xs text-muted-foreground"
        >
          <X className="mr-1 h-3 w-3" />
          Limpar filtros
        </Button>
        <DataTableViewOptions table={table} />
      </div>
    </div>
  );
}
